/**
 * =========================================================================
 * PLAYER NAVIGATION CONFIGURATION
 * =========================================================================
 * 
 * This file defines the navigation menu items for player users.
 * Currently minimal but structured for future expansion as the platform grows.
 * 
 * Navigation Features:
 * - Team collaboration tools
 * - Player-specific features
 * - Future: Training modules, performance tracking
 * 
 * User Access:
 * - player: Basic team member access
 * - Team-dependent features based on membership status
 * 
 * Note: Players with teams get access to organization navigation,
 * while players without teams see limited navigation focused on team joining.
 * =========================================================================
 */

/**
 * Player Navigation Items
 * 
 * Each navigation item includes:
 * - name: Display name for the menu item
 * - path: Route path for navigation
 * - icon: Inline SVG icon markup
 * 
 * Current Categories:
 * - Team Collaboration (Teammates)
 * 
 * Future Categories (Planned):
 * - Performance Tracking
 * - Training Modules
 * - Personal Statistics
 * - Team Communication
 */
const playerNav = [
    // ==================== TEAM MANAGEMENT ====================
  {
    nameKey: "player.myTeam",
    path: "/player/team-requests",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
      <path d="M72 88a56 56 0 1 1 112 0A56 56 0 1 1 72 88zM64 245.7C54 256.9 48 271.8 48 288s6 31.1 16 42.3l0-84.7zm144.4-49.3C178.7 222.7 160 261.2 160 304c0 34.3 12 65.8 32 90.5l0 21.5c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32l0-26.8C26.2 371.2 0 332.7 0 288c0-61.9 50.1-112 112-112l32 0c24 0 46.2 7.5 64.4 20.3zM448 416l0-21.5c20-24.7 32-56.2 32-90.5c0-42.8-18.7-81.3-48.4-107.7C449.8 183.5 472 176 496 176l32 0c61.9 0 112 50.1 112 112c0 44.7-26.2 83.2-64 101.2l0 26.8c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32zm8-328a56 56 0 1 1 112 0A56 56 0 1 1 456 88zM576 245.7l0 84.7c10-11.3 16-26.1 16-42.3s-6-31.1-16-42.3zM320 32a64 64 0 1 1 0 128 64 64 0 1 1 0-128zM240 304c0 16.2 6 31 16 42.3l0-84.7c-10 11.3-16 26.1-16 42.3zm144-42.3l0 84.7c10-11.3 16-26.1 16-42.3s-6-31.1-16-42.3zM448 304c0 44.7-26.2 83.2-64 101.2l0 42.8c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32l0-42.8c-37.8-18-64-56.5-64-101.2c0-61.9 50.1-112 112-112l32 0c61.9 0 112 50.1 112 112z"/>
    </svg>`,
  },

  // ==================== EVENTS & MATCHES ====================
  {
    nameKey: "player.events",
    path: "/player/events",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
      <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z"/>
    </svg>`,
  },
  // ==================== SOCIAL PLATFORM (for verified players) ====================
  {
    nameKey: "feed.title",
    path: "/feed",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
      <path d="M256 0L576 0c35.3 0 64 28.7 64 64l0 224c0 35.3-28.7 64-64 64l-320 0c-35.3 0-64-28.7-64-64l0-224c0-35.3 28.7-64 64-64zM476 106.7C471.5 100 464 96 456 96s-15.5 4-20 10.7l-56 84L362.7 169c-4.6-5.7-11.5-9-18.7-9s-14.2 3.3-18.7 9l-64 80c-5.8 7.2-6.9 17.1-2.9 25.4s12.4 13.6 21.6 13.6l80 0 48 0 144 0c8.9 0 17-4.9 21.2-12.7s3.7-17.3-1.2-24.6l-96-144zM336 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zM64 128l96 0 0 256 0 32c0 17.7 14.3 32 32 32l128 0c17.7 0 32-14.3 32-32l0-32 160 0 0 64c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 192c0-35.3 28.7-64 64-64zm8 64c-8.8 0-16 7.2-16 16l0 16c0 8.8 7.2 16 16 16l16 0c8.8 0 16-7.2 16-16l0-16c0-8.8-7.2-16-16-16l-16 0zm0 104c-8.8 0-16 7.2-16 16l0 16c0 8.8 7.2 16 16 16l16 0c8.8 0 16-7.2 16-16l0-16c0-8.8-7.2-16-16-16l-16 0zm0 104c-8.8 0-16 7.2-16 16l0 16c0 8.8 7.2 16 16 16l16 0c8.8 0 16-7.2 16-16l0-16c0-8.8-7.2-16-16-16l-16 0zm336 16l0 16c0 8.8 7.2 16 16 16l16 0c8.8 0 16-7.2 16-16l0-16c0-8.8-7.2-16-16-16l-16 0c-8.8 0-16 7.2-16 16z"/>
    </svg>`,
    requiresVerification: true, // Only show for verified players
  },

  // ==================== PARENT MANAGEMENT ====================
  {
    nameKey: "player.parents.title",
    path: "/player/parents",
    icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
      <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3zM609.3 512H471.4c5.4-9.4 8.6-20.3 8.6-32v-8c0-60.7-27.1-115.2-69.8-151.8c2.4-.1 4.7-.2 7.1-.2h61.4C567.8 320 640 392.2 640 481.3c0 17-13.8 30.7-30.7 30.7zM432 256c-31 0-59-12.6-79.3-32.9C372.4 196.5 384 163.6 384 128c0-26.8-6.6-52.1-18.3-74.3C384.3 40.1 407.2 32 432 32c61.9 0 112 50.1 112 112s-50.1 112-112 112z"/>
    </svg>`,
  },

  // ==================== FUTURE NAVIGATION ITEMS ====================
  // Uncomment and implement as features are added
  
  // {
  //   name: "Performance",
  //   path: "/player/performance",
  //   icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
  //     <path d="M496 256c0 137-111 248-248 248S0 393 0 256S111 8 248 8s248 111 248 248zM248 48C139.3 48 48 139.3 48 248s91.3 200 200 200s200-91.3 200-200S356.7 48 248 48zm8 152V120c0-13.3-10.7-24-24-24s-24 10.7-24 24v80c0 8.8 4.8 16.9 12.5 21.2l96 64c11.1 7.4 26.1 4.4 33.5-6.7s4.4-26.1-6.7-33.5L256 200z"/>
  //   </svg>`,
  // },

  // {
  //   name: "Training",
  //   path: "/player/training",
  //   icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
  //     <path d="M96 64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V224v64V448c0 17.7-14.3 32-32 32H128c-17.7 0-32-14.3-32-32V384H64c-17.7 0-32-14.3-32-32V288c0-17.7 14.3-32 32-32H96V64zm448 0v32h32c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H544v64V448c0 17.7-14.3 32-32 32H480c-17.7 0-32-14.3-32-32V288 224 64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32zM416 224v64H224V224H416z"/>
  //   </svg>`,
  // },

  // {
  //   name: "Statistics",
  //   path: "/player/stats",
  //   icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
  //     <path d="M160 80c0-26.5 21.5-48 48-48h32c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H208c-26.5 0-48-21.5-48-48V80zM0 272c0-26.5 21.5-48 48-48H80c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V272zM368 96h32c26.5 0 48 21.5 48 48V432c0 26.5-21.5 48-48 48H368c-26.5 0-48-21.5-48-48V144c0-26.5 21.5-48 48-48z"/>
  //   </svg>`,
  // },

  // {
  //   name: "Communications",
  //   path: "/player/chat",
  //   icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
  //     <path d="M208 352c114.9 0 208-78.8 208-176S322.9 0 208 0S0 78.8 0 176c0 38.6 14.7 74.3 39.6 103.4c-3.5 9.4-8.7 17.7-14.2 24.7c-4.8 6.2-9.7 11-13.3 14.3c-1.8 1.6-3.3 2.9-4.3 3.7c-.5 .4-.9 .7-1.1 .8l-.2 .2 0 0 0 0C1 327.2-1.4 334.4 .8 340.9S9.1 352 16 352c21.8 0 43.8-5.6 62.1-16.3c.2-.1 .3-.2 .4-.2c15.1-8.2 26.6-16.9 35.2-26.6c13.6 2.7 28.2 4.1 43.2 4.1zm327.3 137.9c5.8-4.7 11.1-10.2 15.8-16.6c.2-.3 .4-.5 .6-.8c10.9-14.1 17.4-30.2 17.4-47.6c0-114.2-114.9-207-256-207S0 310.8 0 425s114.9 207 256 207c28.4 0 55.5-4.1 80.4-11.7c.2-.1 .4-.1 .6-.2c18.3 10.7 40.3 16.3 62.1 16.3c6.9 0 13.1-4.5 15.2-11.1c2.1-6.6-.2-13.8-5.8-17.9c0 0 0 0-.1-.1l-.2-.2c-.2-.2-.6-.5-1.1-.8c-1-.8-2.5-2-4.3-3.7c-3.6-3.3-8.5-8.1-13.3-14.3c-5.5-7-10.7-15.4-14.2-24.7c24.9-29 39.6-64.7 39.6-103.4z"/>
  //   </svg>`,
  // },
];

export default playerNav;
  